import { EProductTourTargets, EYearMonths } from '@constants';
import { dateProvider } from '@utils';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { EmptyRanking } from './EmptyRanking';
import {
  HeaderWrapper,
  RankingListWrapper,
  TitleWrapper,
  ViewMoreButton,
  Wrapper
} from './RankingWidget.styles';

import { CoursesForStudentSelector } from '@/components/Form';
import { LoadingCircleSpinner } from '@/components/Loaders';
import { RankingListItem } from '@/components/Ranking';
import { useProfile, useRankingByCourse } from '@/store';

const LOADER_SIZE = 32;

export const RankingWidget = () => {
  const { t } = useTranslation(['StudentDashboardTranslations', 'DefaultTranslations']);
  const navigateTo = useNavigate();
  const { profile } = useProfile();
  const {
    ranking = [],
    myPosition,
    selectedMonth,
    setSelectedMonth,
    isLoading,
    course,
    subscriptionsList,
    setCourse
  } = useRankingByCourse();

  const isMe = (rankingId: string) => rankingId === profile?._id;

  const isEmpty = useMemo(() => !ranking?.length, [ranking]);

  const handleGoToRanking = () => {
    navigateTo('/ranking');
  };
  useEffect(() => {
    setSelectedMonth(dateProvider.now().getMonth());
  }, [selectedMonth]);

  const currentMonth = useMemo(() => {
    const monthsArray = Object.values(EYearMonths);
    return monthsArray[selectedMonth];
  }, [selectedMonth]);

  const podiumPosition = useMemo(() => {
    return ranking.slice(0, 3);
  }, [ranking]);

  const isMeInPodium = useMemo(() => {
    return myPosition >= 0 && myPosition <= 2;
  }, [myPosition]);

  const myRanking = useMemo(() => {
    if (!myPosition) {
      return null;
    }
    return ranking[myPosition];
  }, [ranking, myPosition]);

  const handleCourseChange = (courseId: string) => {
    const foundCourse = subscriptionsList?.find(
      subscription => subscription.course._id === courseId
    )?.course;

    if (foundCourse) {
      setCourse(foundCourse);
    }
  };

  return (
    <Wrapper id={EProductTourTargets.RANKINGS_WIDGET}>
      <HeaderWrapper>
        <TitleWrapper>
          {t('RANKING_OF', { month: t(`DefaultTranslations:${currentMonth}`) })}
        </TitleWrapper>
        <ViewMoreButton onClick={handleGoToRanking}>{t('SEE_RANKING')}</ViewMoreButton>
      </HeaderWrapper>
      <CoursesForStudentSelector
        value={course?._id}
        onChange={value => handleCourseChange(value as string)}
      />
      <RankingListWrapper>
        {isLoading && <LoadingCircleSpinner size={LOADER_SIZE} />}
        {!isLoading && course && isEmpty ? (
          <EmptyRanking courseName={course.name} />
        ) : (
          <>
            {podiumPosition.map((item, index) => (
              <RankingListItem
                isMe={isMe(item._id)}
                key={item._id}
                position={index + 1}
                rankingItem={item}
              />
            ))}
            {!isMeInPodium && myRanking && (
              <RankingListItem
                isMe={true}
                key={myRanking._id}
                position={myPosition + 1}
                rankingItem={myRanking}
              />
            )}
          </>
        )}
      </RankingListWrapper>
    </Wrapper>
  );
};
